// Quyền thao tác trên từng tài liệu + các hành động có xác nhận (duyệt, từ chối, thu hồi, xóa, gửi duyệt, rút lại)
import { useState } from 'react';
import { Modal, message } from 'antd';
import type { Doc } from '../data';
import { docState, isContentEditable, isDeletable } from '../logic';
import useDocsStore from '../store';
import { useDocAuth } from '../utils';
import ReasonModal from './ReasonModal';

export default function useDocActions() {
  const { me, can } = useDocAuth();
  const s = useDocsStore();
  const [modal, ctx] = Modal.useModal();
  const [reasonFor, setReasonFor] = useState<{ kind: 'reject' | 'revoke'; doc: Doc } | null>(null);

  const mine = (d: Doc) => d.createdBy === me.id;
  const perm = {
    /** Mở màn sửa: nội dung (chưa ban hành) hoặc chỉ phân quyền (đã ban hành) */
    edit: (d: Doc) => !d.deletedAt && d.status !== 'revoked' && !d.replacedById && (mine(d) || can('edit')),
    editContent: (d: Doc) => !d.deletedAt && isContentEditable(d) && (mine(d) || can('edit')),
    delete: (d: Doc) => !d.deletedAt && ((mine(d) && (d.status === 'draft' || d.status === 'rejected')) || can('delete')),
    submit: (d: Doc) => !d.deletedAt && (d.status === 'draft' || d.status === 'rejected') && (mine(d) || can('edit')),
    withdraw: (d: Doc) => !d.deletedAt && d.status === 'pending' && mine(d),
    approve: (d: Doc) => !d.deletedAt && d.status === 'pending' && can('approve'),
    revoke: (d: Doc) => !d.deletedAt && d.status === 'published' && !d.replacedById && can('approve'),
    reissue: (d: Doc) => !d.deletedAt && d.status === 'published' && !d.replacedById && can('create') && (mine(d) || can('edit')),
    comment: (d: Doc) => !d.deletedAt && d.status === 'draft' && d.draftMode,
  };

  const approve = (d: Doc) =>
    modal.confirm({
      title: 'Duyệt & ban hành tài liệu?',
      content: (
        <div className="text-sm">
          <b>{d.code}</b> — {d.title}
          {d.replacesId && <div className="mt-2 text-amber-700">Tài liệu này ban hành lại: bản cũ sẽ chuyển sang “Bị thay thế”.</div>}
          {d.emailNotify && <div className="mt-2 text-slate-500">Hệ thống sẽ gửi email thông báo tới người được chia sẻ.</div>}
        </div>
      ),
      okText: 'Duyệt & ban hành',
      cancelText: 'Hủy',
      onOk: () => {
        s.approveDoc(d.id, me);
        message.success(`Đã ban hành ${d.code}`);
      },
    });

  const remove = (d: Doc, after?: () => void) => {
    if (!isDeletable(d)) {
      modal.warning({
        title: 'Không thể xóa tài liệu đang có hiệu lực',
        content: 'Tài liệu đã ban hành và còn/sắp có hiệu lực. Hãy thu hồi tài liệu trước khi xóa.',
        okText: 'Đã hiểu',
      });
      return;
    }
    modal.confirm({
      title: 'Chuyển tài liệu vào thùng rác?',
      content: <span className="text-sm"><b>{d.code}</b> — {d.title}. Có thể khôi phục trong 30 ngày.</span>,
      okText: 'Xóa',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: () => {
        s.deleteDoc(d.id, me);
        message.success('Đã chuyển vào thùng rác');
        after?.();
      },
    });
  };

  const submit = (d: Doc) => {
    if (!d.files.length) {
      modal.warning({ title: 'Chưa có file đính kèm', content: 'Cần ít nhất 1 file đính kèm trước khi gửi duyệt. Hãy sửa tài liệu và thêm file.', okText: 'Đã hiểu' });
      return;
    }
    s.submitDoc(d.id, me);
    message.success('Đã gửi duyệt');
  };

  const withdraw = (d: Doc) =>
    modal.confirm({
      title: 'Rút lại tài liệu về dự thảo?', content: 'Tài liệu sẽ không còn trong danh sách chờ duyệt.', okText: 'Rút lại', cancelText: 'Hủy',
      onOk: () => { s.withdrawDoc(d.id, me); message.success('Đã rút lại về dự thảo'); },
    });

  const modals = (
    <>
      {ctx}
      <ReasonModal
        open={!!reasonFor}
        title={reasonFor?.kind === 'reject' ? 'Từ chối duyệt tài liệu' : 'Thu hồi tài liệu'}
        description={reasonFor && (
          <>
            <b>{reasonFor.doc.code}</b> — {reasonFor.doc.title}
            {reasonFor.kind === 'revoke' && <div className="mt-1 text-rose-600">Tài liệu thu hồi sẽ hết hiệu lực ngay, người được chia sẻ vẫn xem được kèm lý do thu hồi.</div>}
          </>
        )}
        okText={reasonFor?.kind === 'reject' ? 'Từ chối' : 'Thu hồi'}
        danger
        onCancel={() => setReasonFor(null)}
        onOk={reason => {
          if (!reasonFor) return;
          if (reasonFor.kind === 'reject') s.rejectDoc(reasonFor.doc.id, reason, me);
          else s.revokeDoc(reasonFor.doc.id, reason, me);
          message.success(reasonFor.kind === 'reject' ? 'Đã từ chối, tài liệu trả về người tạo' : 'Đã thu hồi tài liệu');
          setReasonFor(null);
        }}
      />
    </>
  );

  return {
    perm,
    state: docState,
    approve,
    reject: (d: Doc) => setReasonFor({ kind: 'reject', doc: d }),
    revoke: (d: Doc) => setReasonFor({ kind: 'revoke', doc: d }),
    remove,
    submit,
    withdraw,
    modal,
    modals,
  };
}
